import axios from 'axios';

// Use environment variable for dynamic API URL
const API_URL = process.env.REACT_APP_API_BASE_URL;

const inFlightGets = new Map();

const requestKey = (url, params = {}) => {
  const query = new URLSearchParams();

  Object.entries(params)
    .sort(([left], [right]) => left.localeCompare(right))
    .forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      query.append(key, String(value));
    });

  const serializedParams = query.toString();
  return serializedParams ? `${url}?${serializedParams}` : url;
};

const getOnce = (url, requestConfig = {}) => {
  const key = requestKey(url, requestConfig.params);
  const pendingRequest = inFlightGets.get(key);
  if (pendingRequest) return pendingRequest;

  const request = axios.get(url, requestConfig);
  inFlightGets.set(key, request);
  request.then(
    () => {
      if (inFlightGets.get(key) === request) inFlightGets.delete(key);
    },
    () => {
      if (inFlightGets.get(key) === request) inFlightGets.delete(key);
    }
  );

  return request;
};

// Products API
export const getProductsByCategory = async (categoryId, subcategoryId = null) => {
  const params = { category_id: categoryId };
  if (subcategoryId) {
    params.subcategory_id = subcategoryId;
  }
  return await axios.get(`${API_URL}/products`, { params });
};

export const getProducts = async (requestConfig = {}) => {
  return getOnce(`${API_URL}/products`, requestConfig);
};

export const getProduct = async (productId, requestConfig = {}) => {
  return getOnce(`${API_URL}/products/${productId}`, requestConfig);
};

export const trackProductClick = async (productId) => {
  return axios.post(`${API_URL}/products/${productId}/click`);
};

export const askAssistant = async ({ question, sessionId }) => {
  return axios.post(`${API_URL}/assistant`, {
    question,
    session_id: sessionId,
  });
};

export const createAssistantSession = async () => {
  return axios.post(`${API_URL}/assistant/session`);
};

// Categories API
export const getCategories = async (requestConfig = {}) => {
  return getOnce(`${API_URL}/categories`, requestConfig);
};

// Subcategories API
export const getSubcategories = async (categoryId) => {
  return axios.get(`${API_URL}/subcategories?category_id=${categoryId}`);
};

export const getSubsubcategories = async (subcategoryId) => {
  const params = subcategoryId ? `?subcategory_id=${subcategoryId}` : '';
  return axios.get(`${API_URL}/subsubcategories${params}`);
};

// Attributes API
export const getAttributes = async (categoryId, subcategoryId, subsubcategoryId) => {
  const params = new URLSearchParams();
  if (categoryId) params.append('category_id', categoryId);
  if (subcategoryId) params.append('subcategory_id', subcategoryId);
  if (subsubcategoryId) params.append('subsubcategory_id', subsubcategoryId);

  const response = await axios.get(`${API_URL}/attributes?${params.toString()}`);
  return response;
};

// Filtered Products API
export const getFilteredProducts = async ({
  categoryId,
  subcategoryId,
  subcategoryIds,
  subsubcategoryId,
  filters,
  priceRange,
}, requestConfig = {}) => {
  const sanitizedFilters = Object.entries(filters || {}).reduce((acc, [key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      acc[key] = value;
    }
    return acc;
  }, {});

  const params = {
    price_min: priceRange[0], // Minimum price
    price_max: priceRange[1], // Maximum price
  };

  if (Object.keys(sanitizedFilters).length > 0) {
    params.filters = JSON.stringify(sanitizedFilters); // Serialize only active filters
  }

  if (categoryId) {
    params.category_id = categoryId;
  }
  if (subcategoryId) {
    params.subcategory_id = subcategoryId;
  }
  if (Array.isArray(subcategoryIds) && subcategoryIds.length > 0) {
    params.subcategory_ids = subcategoryIds.join(',');
  }
  if (subsubcategoryId) {
    params.subsubcategory_id = subsubcategoryId;
  }

  try {
    const response = await axios.get(`${API_URL}/products`, { ...requestConfig, params });
    return response; // Return API response
  } catch (error) {
    console.error('Error fetching filtered products:', error);
    throw error; // Re-throw error to be handled by the caller
  }
};
